import { useEffect, useState } from 'react';
import { type AddressSelection, formatAddress } from '../../lib/address';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { AddressAutocomplete } from './AddressAutocomplete';

export type AddressEditorValue = AddressSelection;

type AddressEditorProps = {
  value: AddressEditorValue;
  onChange: (value: AddressEditorValue) => void;
  idPrefix: string;
  disabled?: boolean;
  apiKey?: string;
  searchLabel?: string;
};

function editableAddress(
  value: AddressEditorValue,
  field: 'street' | 'locality' | 'region' | 'postalCode' | 'country',
  nextValue: string
): AddressEditorValue {
  const next = { ...value, [field]: nextValue };
  return {
    ...next,
    formattedAddress: formatAddress(next),
    googlePlaceId: null,
    latitude: null,
    longitude: null,
  };
}

/**
 * Provider-neutral address form. Google selection supplies a useful Australian default, while
 * every normalized component remains visible and editable for real-world corrections.
 */
export function AddressEditor({
  value,
  onChange,
  idPrefix,
  disabled = false,
  apiKey,
  searchLabel = 'Find address',
}: AddressEditorProps) {
  const [search, setSearch] = useState(value.formattedAddress || formatAddress(value));

  useEffect(() => {
    setSearch(value.formattedAddress || formatAddress(value));
  }, [value]);

  function update(
    field: 'street' | 'locality' | 'region' | 'postalCode' | 'country',
    nextValue: string
  ) {
    onChange(editableAddress(value, field, nextValue));
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-search`}>{searchLabel}</Label>
        <AddressAutocomplete
          apiKey={apiKey}
          className="text-sm"
          id={`${idPrefix}-search`}
          onChange={setSearch}
          onSelectAddress={(selection) => onChange(selection)}
          placeholder="Search Australian addresses"
          value={search}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <AddressField
          disabled={disabled}
          id={`${idPrefix}-street`}
          label="Street address"
          onChange={(nextValue) => update('street', nextValue)}
          value={value.street}
        />
        <AddressField
          disabled={disabled}
          id={`${idPrefix}-locality`}
          label="Suburb"
          onChange={(nextValue) => update('locality', nextValue)}
          value={value.locality}
        />
        <AddressField
          disabled={disabled}
          id={`${idPrefix}-region`}
          label="State / territory"
          onChange={(nextValue) => update('region', nextValue)}
          value={value.region}
        />
        <AddressField
          disabled={disabled}
          id={`${idPrefix}-postal-code`}
          label="Postcode"
          onChange={(nextValue) => update('postalCode', nextValue)}
          value={value.postalCode}
        />
        <AddressField
          className="sm:col-span-2"
          disabled={disabled}
          id={`${idPrefix}-country`}
          label="Country"
          onChange={(nextValue) => update('country', nextValue)}
          value={value.country}
        />
      </div>
    </div>
  );
}

function AddressField({
  className,
  disabled,
  id,
  label,
  onChange,
  value,
}: {
  className?: string;
  disabled: boolean;
  id: string;
  label: string;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <div className={`space-y-2 ${className || ''}`}>
      <Label className="text-xs font-semibold" htmlFor={id}>
        {label}
      </Label>
      <Input
        className="bg-background text-sm"
        disabled={disabled}
        id={id}
        onChange={(event) => onChange(event.target.value)}
        value={value}
      />
    </div>
  );
}
